// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { NavHashLink as NavLink, HashLink as Link } from 'react-router-hash-link';

import styles from '../../styles/PatientSidebarMenu.scss';

type Props = {
  match: Object,
  location: Object,
  showCounseling: bool,
  infantMeasurements: Object[],
  childMeasurements: Object[],
  wellChildChecks: Object[],
}

class PatientSidebarComponent extends Component<Props> {
  render() {
    const { match } = this.props;
    const path = window.location.pathname;

    // Highlight growth charts label if weight has decreased in the last two data points.
    const measurements = this.props.infantMeasurements.concat(this.props.childMeasurements)
      .filter(measurement => measurement.weight && measurement.date)
      .map(measurement => ({ weight: measurement.weight, date: measurement.date }));
    const wccMeasurements = this.props.wellChildChecks.filter(wcc => wcc.vitals.weight && wcc.date)
      .map(wcc => ({ weight: wcc.vitals.weight, date: wcc.date }));
    const allMeasurements = measurements.concat(wccMeasurements)
      .sort((m1, m2) => (m1.date < m2.date ? -1 : 1))
      .slice(-2);
    const decliningWeight = allMeasurements.length > 1 && allMeasurements[0].weight > allMeasurements[1].weight;

    return (
      <div className={styles.sidebarMenu}>
        <ul className={styles.menuList}>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/basicInformation`}>
              Basic Information
            </NavLink>
            {path.includes('basicInformation')
              && <ul className={styles.subMenu}>
                <li>
                  <Link to={`${match.url}/basicInformation#generalInfo`} smooth>General Info</Link>
                </li>
                <li>
                  <Link to={`${match.url}/basicInformation#socialHistory`} smooth>Social Hx</Link>
                </li>
                <li>
                  <Link to={`${match.url}/basicInformation#allergies`} smooth>Allergies</Link>
                </li>
                <li>
                  <Link to={`${match.url}/basicInformation#significantHistory`} smooth>Significant Hx</Link>
                </li>
                <li>
                  <Link to={`${match.url}/basicInformation#houseHistory`} smooth>House Hx</Link>
                </li>
                <li>
                  <Link to={`${match.url}/basicInformation#hospHistory`} smooth>Hospitalization Hx</Link>
                </li>
                <li>
                  <Link to={`${match.url}/basicInformation#dietHistory`} smooth>Diet Hx</Link>
                </li>
              </ul>
            }
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/todos`}>
              Work List
            </NavLink>
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/currentProblems`}>
              Current Dx/Tx
            </NavLink>
            {path.includes('currentProblems')
              && <ul className={styles.subMenu}>
                <li>
                  <Link to={`${match.url}/currentProblems#acute`} smooth>Acute</Link>
                </li>
                <li>
                  <Link to={`${match.url}/currentProblems#chronic`} smooth>Chronic</Link>
                </li>
              </ul>
            }
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/resolvedProblems`}>
              Resolved Dx/Tx
            </NavLink>
            {path.includes('resolvedProblems')
              && <ul className={styles.subMenu}>
                <li>
                  <Link to={`${match.url}/resolvedProblems#diagnoses`} smooth>Diagnoses</Link>
                </li>
                <li>
                  <Link to={`${match.url}/resolvedProblems#medications`} smooth>Medications</Link>
                </li>
                <li>
                  <Link to={`${match.url}/resolvedProblems#interventions`} smooth>Interventions</Link>
                </li>
              </ul>
            }
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/vitals`}>
              Vital Signs
            </NavLink>
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/immunizations`}>
              Immunizations
            </NavLink>
            {path.includes('immunizations')
              && <ul className={styles.subMenu}>
                <li>
                  <Link to={`${match.url}/immunizations#required`} smooth>Required</Link>
                </li>
                <li>
                  <Link to={`${match.url}/immunizations#additional`} smooth>Additional</Link>
                </li>
              </ul>
            }
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/wellChildChecks`}>
              Well Child Checks
            </NavLink>
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/testing`}>
              POC Testing
            </NavLink>
            {path.includes('testing')
              && <ul className={styles.subMenu}>
                <li>
                  <Link to={`${match.url}/testing#hiv`} smooth>HIV</Link>
                </li>
                <li>
                  <Link to={`${match.url}/testing#tb`} smooth>TB</Link>
                </li>
                <li>
                  <Link to={`${match.url}/testing#hemoglobin`} smooth>Hemoglobin</Link>
                </li>
                <li>
                  <Link to={`${match.url}/testing#glucouse`} smooth>Glucose</Link>
                </li>
                <li>
                  <Link to={`${match.url}/testing#syphilis`} smooth>Syphilis</Link>
                </li>
                <li>
                  <Link to={`${match.url}/testing#urinalysis`} smooth>Urinalysis</Link>
                </li>
                <li>
                  <Link to={`${match.url}/testing#covid`} smooth>COVID-19</Link>
                </li>
                <li>
                  <Link to={`${match.url}/testing#misc`} smooth>Misc</Link>
                </li>
              </ul>
            }
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/growthCharts`} exact>
              <span className={decliningWeight ? styles.red : ''}>
                Growth Charts
              </span>
            </NavLink>
            {path.includes('growthCharts')
              && <ul className={styles.subMenu}>
                <li>
                  <NavLink activeClassName={styles.activeLink} to={`${match.url}/growthCharts/infant`}>
                    Birth-36 Months
                  </NavLink>
                  {path.includes('infant')
                    && <ul className={styles.subMenu}>
                      <li>
                        <Link to={`${match.url}/growthCharts/infant#measurements`} smooth>• Measurements</Link>
                      </li>
                      <li>
                        <Link to={`${match.url}/growthCharts/infant#weightAge`} smooth>• Weight-for-Age</Link>
                      </li>
                      <li>
                        <Link to={`${match.url}/growthCharts/infant#lengthAge`} smooth>• Length-for-Age</Link>
                      </li>
                      <li>
                        <Link to={`${match.url}/growthCharts/infant#hcAge`} smooth>• HC-for-Age</Link>
                      </li>
                      <li>
                        <Link to={`${match.url}/growthCharts/infant#weightLength`} smooth>• Weight-for-Length</Link>
                      </li>
                    </ul>
                  }
                </li>
                <li>
                  <NavLink activeClassName={styles.activeLink} to={`${match.url}/growthCharts/child`}>
                    2-20 Years
                  </NavLink>
                  {path.includes('child')
                    && <ul className={styles.subMenu}>
                      <li>
                        <Link to={`${match.url}/growthCharts/child#measurements`} smooth>• Measurements</Link>
                      </li>
                      <li>
                        <Link to={`${match.url}/growthCharts/child#weightAge`} smooth>• Weight-for-Age</Link>
                      </li>
                      <li>
                        <Link to={`${match.url}/growthCharts/child#heightAge`} smooth>• Height-for-Age</Link>
                      </li>
                      <li>
                        <Link to={`${match.url}/growthCharts/child#weightHeight`} smooth>• Weight-for-Height</Link>
                      </li>
                    </ul>
                  }
                </li>
              </ul>
            }
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/externalReports`}>
              External Reports
            </NavLink>
            {path.includes('externalReports')
              && <ul className={styles.subMenu}>
                <li>
                  <Link to={`${match.url}/externalReports#doctor`} smooth>{'Doctor\'s Notes'}</Link>
                </li>
                <li>
                  <Link to={`${match.url}/externalReports#labResults`} smooth>Lab Results</Link>
                </li>
                <li>
                  <Link to={`${match.url}/externalReports#xrays`} smooth>X-Rays</Link>
                </li>
                <li>
                  <Link to={`${match.url}/externalReports#ctscans`} smooth>CT Scans</Link>
                </li>
                <li>
                  <Link to={`${match.url}/externalReports#ultrasounds`} smooth>Ultrasounds</Link>
                </li>
                <li>
                  <Link to={`${match.url}/externalReports#otherImagingReports`} smooth>Other Imaging</Link>
                </li>
                <li>
                  <Link to={`${match.url}/externalReports#socialwork`} smooth>Social Work</Link>
                </li>
                <li>
                  <Link to={`${match.url}/externalReports#misc`} smooth>Misc</Link>
                </li>
              </ul>
            }
          </li>
          <li>
            <NavLink activeClassName={styles.activeLink} to={`${match.url}/aggregatedNotes`}>
              Aggregated Notes
            </NavLink>
          </li>
        </ul>
        {this.props.showCounseling
          && <ul className={styles.menuFooter}>
            <li><NavLink activeClassName={styles.activeLink} to={`${match.url}/counselingReports`} exact>Counseling Reports</NavLink></li>
          </ul>}
        <ul className={styles.menuFooter}>
          <li><NavLink activeClassName={styles.activeLink} to={`${match.url}/newDiagnosis`} exact>New Diagnosis</NavLink></li>
          <li><NavLink activeClassName={styles.activeLink} to={`${match.url}/print`} exact>Print Patient</NavLink></li>
        </ul>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  infantMeasurements: state.patient.data.infantMeasurements,
  childMeasurements: state.patient.data.childMeasurements,
  wellChildChecks: state.patient.data.wellChildChecks,
});

const PatientSidebarMenu = connect(
  mapStateToProps,
)(PatientSidebarComponent);

export default PatientSidebarMenu;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/PatientSidebarMenu.jsx